import { AxiosResponse } from 'axios'
import dayjs, { Dayjs } from 'dayjs'

import { SecurityField } from 'types/securityField'
import { ShiftNote } from 'types/shiftNote'

import { _axios } from './axios'

const FILTER = '/security_fields'
const CREATE = '/security_fields'
const CREATE_SHIFT_NOTE = (id: string) => `/security_fields/${id}/shift_note`
const UPDATE = (id: string) => `/security_fields/update/${id}`
const UPDATE_SHIFT_NOTE = (securityFieldId: string, id: string) =>
  `/security_fields/${securityFieldId}/shift_note/${id}`
const BULK_DELETE = '/security_fields/bulk_delete'
const DELETE_SHIFT_NOTE = (securityFieldId: string, id: string) =>
  `/security_fields/${securityFieldId}/shift_note/${id}`
const FILTER_SHIFT_NOTE_BY_YEAR_MONTH = (id: string) =>
  `/security_fields/${id}/shift_notes`

export const securityFieldApi = () => {
  const filter = async (): Promise<AxiosResponse<SecurityField[]>> => {
    return await _axios()
      .get(FILTER)
      .then((x) => {
        x.data = x.data.securityFields
        return x
      })
  }

  const shiftMail = async (
    securityFieldId: string,
    year: number,
    month: number,
  ): Promise<AxiosResponse<void>> => {
    return await _axios().post(
      `/security_fields/${securityFieldId}/shift_mail`,
      {
        year: year,
        month: month,
      },
    )
  }

  const add = async (
    data: Omit<SecurityField, 'id'>,
  ): Promise<AxiosResponse<void>> => {
    return await _axios().post(CREATE, data)
  }

  const addShiftNote = async (
    securityFieldId: string,
    data: {
      securityId: string
      date: Dayjs
      note: string
    },
  ): Promise<AxiosResponse<void>> => {
    return await _axios().post(CREATE_SHIFT_NOTE(securityFieldId), data)
  }

  const update = async (data: SecurityField): Promise<AxiosResponse<void>> => {
    return await _axios().put(UPDATE(data.id), data)
  }

  const updateShiftNote = async (data: {
    shiftNote: ShiftNote
  }): Promise<AxiosResponse<void>> => {
    return await _axios().put(
      UPDATE_SHIFT_NOTE(data.shiftNote.securityFieldId, data.shiftNote.id),
      data.shiftNote,
    )
  }

  const bulkDelete = async (data: {
    securityFieldIds: string[]
  }): Promise<AxiosResponse<void>> => {
    return await _axios().delete(BULK_DELETE, {
      data,
    })
  }

  const deleteShiftNote = async (
    securityFieldId: string,
    id: string,
  ): Promise<AxiosResponse<void>> => {
    return await _axios().delete(DELETE_SHIFT_NOTE(securityFieldId, id))
  }

  const filterShiftNoteByYearMonth = async (
    securityFieldId: string,
    year: number,
    month: number,
  ): Promise<AxiosResponse<ShiftNote[]>> => {
    const response = await _axios().get(
      FILTER_SHIFT_NOTE_BY_YEAR_MONTH(securityFieldId),
      {
        params: {
          year,
          month,
        },
      },
    )

    response.data = response.data.shiftNotes.map((note: ShiftNote) => ({
      ...note,
      date: dayjs(note.date),
    }))

    return response
  }

  return {
    filter,
    shiftMail,
    add,
    update,
    updateShiftNote,
    bulkDelete,
    deleteShiftNote,
    addShiftNote,
    filterShiftNoteByYearMonth,
  }
}
